import { useMemo } from "react";
import { toSpotPairSymbol } from "@cede/utils";
import { useMarketPairs } from "./useMarketPairs";
import { useTradingViewStore } from "@/store/useTradingViewStore";

type UseActiveTradingPairParams = {
  campaignData?: {
    exchangeIds?: string[] | null;
    tokenSymbol?: string;
    /**
     * Allowed pair symbols for the campaign. When multiple pairs are declared
     * any of these is a valid user selection;
     * the validity check should not collapse to `tokenSymbol` alone.
     */
    pairSymbols?: string[] | null;
  } | null;
};

export const useActiveTradingPair = ({ campaignData }: UseActiveTradingPairParams) => {
  const { exchange, pairSymbol } = useTradingViewStore();
  const campaignMetadata = campaignData;

  // Get active exchange - uses user selection from store if valid, or falls back to campaign default
  const activeExchange = useMemo(() => {
    const exchangeIds = campaignMetadata?.exchangeIds;

    // If user has selected an exchange from the store, validate it belongs to the current campaign
    // This prevents using stale exchange data when navigating between campaigns
    if (exchange && exchangeIds?.includes(exchange.exchangeId)) {
      return exchange;
    }

    // Fallback: Use first exchange from campaign for public/initial data
    const firstExchangeId = exchangeIds && exchangeIds.length > 0 ? exchangeIds[0] : null;
    if (firstExchangeId) {
      return {
        exchangeId: firstExchangeId,
        accountId: firstExchangeId, // Use exchange ID as account ID for public view
      };
    }
    return null;
  }, [exchange, campaignMetadata?.exchangeIds]);

  const { markets } = useMarketPairs(activeExchange?.exchangeId);

  // Get active pair symbol - uses user selection from store if valid, or falls back to campaign default
  const activePairSymbol = useMemo(() => {
    // Build the campaign's allowed-pair set, normalized to spot-style so it
    // can be compared against the synthetic `${base}/${quote}` key built from
    // market entries. When the campaign declares an explicit pair list, that's
    // the source of truth; legacy single-token campaigns fall back to
    // `tokenSymbol` membership below.
    const campaignPairSet = new Set((campaignMetadata?.pairSymbols ?? []).map(toSpotPairSymbol));
    const tokenSymbol = campaignMetadata?.tokenSymbol;

    if (pairSymbol && markets) {
      // Market entries join to spot-style `${base}/${quote}` — perps-format
      // store values never pass this check, so by the time we reach the
      // campaign-membership check the candidate is already spot-style.
      const pairExistsOnExchange = markets.some((market) => `${market.base}/${market.quote}` === pairSymbol.pairSymbol);
      const isAllowedByCampaign =
        campaignPairSet.size > 0
          ? campaignPairSet.has(pairSymbol.pairSymbol)
          : !tokenSymbol || pairSymbol.pairSymbol.includes(tokenSymbol);
      if (pairExistsOnExchange && isAllowedByCampaign) {
        return pairSymbol;
      }
      // Pair from store is not valid for current campaign, fall through to default
    }

    // Fallback for multi-pair campaigns: prefer the first declared pair, if
    // available on this exchange, before falling back to the legacy single-token
    // discovery path. Keeps the multi-token landing UX deterministic.
    if (campaignPairSet.size > 0 && markets) {
      const firstAllowedNormalized = toSpotPairSymbol(campaignMetadata!.pairSymbols![0]!);
      const match = markets.find((market) => `${market.base}/${market.quote}` === firstAllowedNormalized);
      if (match) {
        return {
          pairSymbol: `${match.base}/${match.quote}`,
          precision: match.precision?.price || 8,
          isTradeSquare: false,
        };
      }
    }

    // Legacy single-token fallback: <TokenSymbol>/<stablecoin> on the exchange.
    const stablePairsByPriority = ["USDT", "USD", "USDC", "KRW"];
    const stablePair = markets?.find(
      (market) => stablePairsByPriority.includes(market.quote) && market.base === tokenSymbol,
    );
    if (stablePair && tokenSymbol) {
      return {
        pairSymbol: `${tokenSymbol}/${stablePair.quote}`,
        precision: stablePair.precision?.price || 8,
        isTradeSquare: false,
      };
    }
    return null;
  }, [pairSymbol, campaignMetadata?.tokenSymbol, campaignMetadata?.pairSymbols, markets]);

  return {
    activeExchange,
    activePairSymbol,
  };
};
